// TMX Money's GraphQL: the site's own API, no key (verified 2026-09-21; design.md "Yahoo Finance 与 TMX Money 调查").
// Covers TSX / TSXV (bare symbol), CSE (":CNX"), Cboe Canada (":AQL"), US listings (":US", dots kept: BRK.B:US) and
// Canadian mutual funds by FundSERV code.
// Quotes are the site's ~15-minute delayed feed; fund NAVs carry their publication date. Introspection is off, so
// every field below was confirmed by hand — an unknown field fails the whole request.
//
// CORS: responses carry `access-control-allow-origin: *`, but the preflight answers only money.tmx.com, so a JSON
// POST (which needs a preflight) is blocked in every browser. The request is therefore sent as a form-encoded POST,
// a "simple" request that needs no preflight (2026-09-22).
import type { Asset, Quote } from '@/types';
import { MarketError, marketFetch } from './errors';

const ENDPOINT = 'https://app-money.tmx.com/graphql';
const LABEL = 'TMX Money';

/** TMX's spelling of a listing: bare for TSX / TSXV, venue-suffixed for CSE and Cboe Canada, ":US" for US markets (a bare AAPL is the CDR). */
export function tmxSymbol(listing: Pick<Asset, 'symbol' | 'mic'> & { market?: string }): string {
  const s = listing.symbol.toUpperCase();
  if (listing.market === 'US') return `${s}:US`;
  return listing.mic === 'XCNQ' ? `${s}:CNX` : listing.mic === 'NEOE' ? `${s}:AQL` : s;
}

export interface TmxListing {
  name: string;
  datatype: string; // 'equity' | 'etf' | 'mutual fund' …
  exchangeName?: string;
  exchangeCode?: string;
  price?: number;
  currency?: string;
}

/** What TMX knows about one symbol (its spelling, e.g. "AAPL:US"), or null. Lets a US ticker be added with no key. */
export async function tmxLookup(symbol: string, signal?: AbortSignal): Promise<TmxListing | null> {
  const query = 'query Q($s: String!) { r: getQuoteBySymbol(symbol: $s, locale: "en") { symbol name datatype exchangeCode exchangeName price currency } }';
  const res = await marketFetch(ENDPOINT, { method: 'POST', body: new URLSearchParams({ query, variables: JSON.stringify({ s: symbol }) }), signal }, LABEL);
  const json = (await res.json()) as { data?: { r?: { name?: string | null; datatype?: string | null; exchangeCode?: string | null; exchangeName?: string | null; price?: number | null; currency?: string | null } | null } | null };
  const r = json.data?.r;
  if (!r?.name) return null;
  return { name: r.name, datatype: r.datatype ?? '', exchangeName: r.exchangeName ?? undefined, exchangeCode: r.exchangeCode ?? undefined, price: pos(r.price), currency: r.currency ?? undefined };
}

export interface TmxRequest {
  key: string; // quote key the result is stored under
  symbol: string; // TMX spelling (tmxSymbol) or a FundSERV code
  kind: 'equity' | 'fund';
}

/** A getQuoteForSymbols row; an unknown symbol comes back as a row of nulls, not an error. */
export interface TmxQuoteRow {
  symbol?: string | null;
  price?: number | null;
  currency?: string | null;
  openPrice?: number | null;
  dayHigh?: number | null;
  dayLow?: number | null;
  prevClose?: number | null;
  priceChange?: number | null;
  percentChange?: number | null;
  lasttradedatetime?: string | null;
}

/** getQuoteBySymbol for a fund: `price` is the NAV and `datetime` its publication date; there is no daily change. */
export interface TmxFundRow {
  symbol?: string | null;
  name?: string | null;
  price?: number | null;
  currency?: string | null;
  datetime?: string | null;
  datatype?: string | null;
}

export interface TmxHistoryRow {
  datetime?: string | null; // YYYY-MM-DD
  closePrice?: number | null;
}

export interface TmxFund {
  quote: Quote;
  name: string;
}

const pos = (v: number | null | undefined): number | undefined => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined);
const num = (v: number | null | undefined): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** Pure; exported for tests. */
export function parseTmxEquity(key: string, row: TmxQuoteRow): Quote | null {
  const price = pos(row.price);
  if (price == null) return null;
  const asOf = row.lasttradedatetime ? Date.parse(row.lasttradedatetime) : NaN;
  return {
    key,
    price,
    currency: (row.currency ?? 'CAD').toUpperCase(),
    asOf: Number.isFinite(asOf) ? asOf : Date.now(),
    change: num(row.priceChange),
    changePct: num(row.percentChange),
    previousClose: pos(row.prevClose),
    open: pos(row.openPrice),
    high: pos(row.dayHigh),
    low: pos(row.dayLow),
    source: 'tmx',
    freshness: 'delayed15',
    stale: false,
  };
}

/**
 * Pure; exported for tests. The NAV's own date comes from `datetime`; the change is against the previous
 * distinct NAV in the history, whose newest row merely repeats the last NAV under today's date.
 */
export function parseTmxFund(key: string, row: TmxFundRow | null | undefined, history: TmxHistoryRow[] = []): TmxFund | null {
  const price = pos(row?.price);
  if (row == null || price == null) return null;
  const navDate = row.datetime?.slice(0, 10);
  const asOf = row.datetime ? Date.parse(row.datetime) : NaN;
  const previous = history
    .filter((h): h is { datetime: string; closePrice: number } => !!h.datetime && pos(h.closePrice) != null && (!navDate || h.datetime < navDate))
    .sort((a, b) => (a.datetime < b.datetime ? 1 : -1))[0];
  const quote: Quote = {
    key,
    price,
    currency: (row.currency ?? 'CAD').toUpperCase(),
    asOf: Number.isFinite(asOf) ? asOf : Date.now(),
    change: previous ? price - previous.closePrice : undefined,
    changePct: previous ? ((price - previous.closePrice) / previous.closePrice) * 100 : undefined,
    previousClose: previous?.closePrice,
    source: 'tmx',
    freshness: 'nav',
    stale: false,
  };
  return { quote, name: row.name?.trim() || row.symbol?.trim() || key };
}

const EQUITY_FIELDS = 'symbol price currency openPrice dayHigh dayLow prevClose priceChange percentChange lasttradedatetime';
const FUND_FIELDS = 'symbol name price currency datetime datatype';

interface TmxResponse {
  data?: Record<string, unknown> | null;
  errors?: { message?: string }[];
}

/**
 * One request for everything: equities through the batch field, each fund through its own aliased lookup plus a
 * month of NAV history for the day change. A symbol TMX does not know is simply absent from the result.
 */
export async function tmxQuotes(requests: TmxRequest[], signal?: AbortSignal): Promise<{ quotes: Quote[]; funds: Record<string, TmxFund> }> {
  const equities = requests.filter((r) => r.kind === 'equity');
  const funds = requests.filter((r) => r.kind === 'fund');
  if (requests.length === 0) return { quotes: [], funds: {} };

  const vars: string[] = [];
  const fields: string[] = [];
  const variables: Record<string, unknown> = {};
  if (equities.length > 0) {
    vars.push('$eq: [String]');
    fields.push(`eq: getQuoteForSymbols(symbols: $eq) { ${EQUITY_FIELDS} }`);
    variables.eq = equities.map((r) => r.symbol);
  }
  funds.forEach((r, i) => {
    vars.push(`$f${i}: String!`);
    fields.push(`f${i}: getQuoteBySymbol(symbol: $f${i}, locale: "en") { ${FUND_FIELDS} }`);
    fields.push(`h${i}: getCompanyPriceHistory(symbol: $f${i}) { datetime closePrice }`);
    variables[`f${i}`] = r.symbol;
  });
  const query = `query Q(${vars.join(', ')}) { ${fields.join(' ')} }`;

  // No explicit content-type: URLSearchParams makes the browser send application/x-www-form-urlencoded, which skips the preflight.
  const res = await marketFetch(ENDPOINT, { method: 'POST', body: new URLSearchParams({ query, variables: JSON.stringify(variables) }), signal }, LABEL);
  const json = (await res.json()) as TmxResponse;
  // Per-symbol misses arrive as errors next to a null alias; only a response without data is a real failure.
  if (!json.data) throw new MarketError('upstream', `${LABEL}: ${json.errors?.[0]?.message ?? 'empty response'}`);

  const quotes: Quote[] = [];
  const rows = new Map(((json.data.eq as TmxQuoteRow[] | null | undefined) ?? []).map((x) => [x.symbol?.toUpperCase() ?? '', x]));
  for (const r of equities) {
    const row = rows.get(r.symbol.toUpperCase());
    const q = row ? parseTmxEquity(r.key, row) : null;
    if (q) quotes.push(q);
  }
  const found: Record<string, TmxFund> = {};
  funds.forEach((r, i) => {
    const fund = parseTmxFund(r.key, json.data![`f${i}`] as TmxFundRow | null, (json.data![`h${i}`] as TmxHistoryRow[] | null) ?? []);
    if (fund) {
      quotes.push(fund.quote);
      found[r.key] = fund;
    }
  });
  return { quotes, funds: found };
}

/** Name and latest NAV for one FundSERV code, or null when TMX does not list it. */
export async function tmxFund(code: string, signal?: AbortSignal): Promise<TmxFund | null> {
  const key = `fund:${code.toUpperCase()}`;
  const { funds } = await tmxQuotes([{ key, symbol: code.toUpperCase(), kind: 'fund' }], signal);
  return funds[key] ?? null;
}
